/**
 * WORLD-CLASS ABOUT PAGE
 * Comprehensive profile showcasing Dr. Deepthi Nandan Reddy
 */

import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useSite } from '../contexts/SiteContext';
import { YouTubeEmbed, DR_REDDY_VIDEOS } from '../components/YouTubeEmbed';
import { 
  GraduationCap, 
  Award, 
  Building2, 
  BadgeCheck,
  MapPin,
  Phone,
  Mail,
  Clock,
  Star,
  ExternalLink,
  Calendar,
  Play,
  Quote,
  Users,
  Stethoscope
} from 'lucide-react';

export const About = () => {
  const { theme, doctor, bodyPart } = useSite();

  const timeline = [
    {
      year: '1990s',
      title: 'Medical Education',
      description: 'MBBS and MS (Orthopaedics) from Osmania Medical College, Hyderabad'
    },
    {
      year: '2000s',
      title: 'UK Training',
      description: 'Six-year advanced surgical training in the United Kingdom. MSc (Ortho, UK), FRCS Ed, FRCS (Ortho, UK), CCT'
    },
    {
      year: '2000s',
      title: 'International Fellowships',
      description: 'Advanced fellowships at Mayo Clinic (USA) and Florida Orthopaedic Institute'
    },
    {
      year: '2010s',
      title: 'UK Consultant',
      description: 'Consultant Orthopaedic / Shoulder & Hand Surgeon at University Hospitals of Leicester, UK'
    },
    {
      year: 'Present',
      title: 'Leading Practice in India',
      description: 'Sr. Consultant at Apollo Hospitals, Financial District & Director of Upper Limb Services at Continental Hospital, Hyderabad'
    }
  ];

  const expertise = [
    'Shoulder Arthroscopy & Replacement',
    'Elbow Surgery & Reconstruction',
    'Hand & Wrist Microsurgery',
    'Sports Injury Management',
    'Fracture Care & Trauma',
    'Joint Preservation Techniques'
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{ backgroundColor: theme.primaryHex }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Doctor Image */}
            <div className="order-2 lg:order-1">
              <div className="relative max-w-md mx-auto">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
                  <img
                    src={doctor.doctorImage}
                    alt={doctor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Floating credentials */}
                <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-4 max-w-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.primaryHex}20` }}>
                      <GraduationCap className="w-6 h-6" style={{ color: theme.primaryHex }} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">30+ Years Experience</p>
                      <p className="text-xs text-gray-500">Fellowship trained in UK & USA</p>
                    </div>
                  </div>
                </div>

                {/* Rating badge */}
                <div className="absolute -top-4 -left-4 bg-white rounded-xl shadow-xl p-3">
                  <div className="flex items-center gap-2">
                    <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    <div>
                      <p className="font-bold text-gray-900">{doctor.rating}</p>
                      <p className="text-xs text-gray-500">Google</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2 text-white">
              <p className="text-sm uppercase tracking-wider opacity-80 mb-4">
                About the Specialist
              </p>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {doctor.name}
              </h1>
              <p className="text-xl opacity-90 mb-2">
                {doctor.title}
              </p>
              <p className="text-lg opacity-80 mb-6 italic">
                &ldquo;{doctor.tagline}&rdquo;
              </p>
              
              {/* Qualifications */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Qualifications
                </h3>
                <p className="opacity-90 mb-4">{doctor.credentials}</p>
                
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Fellowships
                </h3>
                <p className="opacity-90">{doctor.fellowships}</p>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-white hover:bg-gray-100" style={{ color: theme.primaryHex }}>
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Consultation
                  </Button>
                </a>
                <a href={doctor.whatsappLink} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    💬 WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold" style={{ color: theme.primaryHex }}>{doctor.experience}</p>
              <p className="text-sm text-gray-400">Years Experience</p>
            </div>
            <div>
              <p className="text-3xl font-bold" style={{ color: theme.primaryHex }}>{doctor.patients_treated}</p>
              <p className="text-sm text-gray-400">Patients Treated</p>
            </div>
            <div>
              <p className="text-3xl font-bold" style={{ color: theme.primaryHex }}>{doctor.surgeries_performed}</p>
              <p className="text-sm text-gray-400">Surgeries</p>
            </div>
            <div>
              <p className="text-3xl font-bold" style={{ color: theme.primaryHex }}>{doctor.rating}</p>
              <p className="text-sm text-gray-400">Google Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Journey */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Professional Journey
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Three decades of excellence in orthopaedic surgery
            </p>

            <div className="relative">
              {/* Timeline line */}
              <div 
                className="absolute left-8 top-0 bottom-0 w-0.5"
                style={{ backgroundColor: `${theme.primaryHex}30` }}
              />
              
              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <div key={index} className="relative pl-20">
                    {/* Timeline dot */}
                    <div 
                      className="absolute left-6 w-5 h-5 rounded-full border-4 border-white"
                      style={{ backgroundColor: theme.primaryHex }}
                    />
                    
                    <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <span 
                        className="text-sm font-bold"
                        style={{ color: theme.primaryHex }}
                      >
                        {item.year}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mt-1">{item.title}</h3>
                      <p className="text-gray-600 mt-2">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hospital Positions */}
      <section className="py-20" style={{ backgroundColor: theme.lightBgHex }}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Current Positions
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Serving patients at leading healthcare institutions
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {doctor.positions?.map((position, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow border-t-4" style={{ borderTopColor: theme.primaryHex }}>
                <CardHeader>
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${theme.primaryHex}15` }}
                  >
                    <Building2 className="w-8 h-8" style={{ color: theme.primaryHex }} />
                  </div>
                  <CardTitle className="text-lg">{position.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-gray-900">{position.organization}</p>
                  <p className="text-sm text-gray-500">{position.location}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Memberships */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Professional Memberships
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Active member of prestigious medical organizations worldwide
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {doctor.memberships?.map((membership, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-gray-50 p-5 rounded-xl hover:shadow-md transition-shadow"
              >
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${theme.primaryHex}15` }}
                >
                  <BadgeCheck className="w-6 h-6" style={{ color: theme.primaryHex }} />
                </div>
                <p className="text-gray-800 font-medium">{membership}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of Expertise */}
      <section className="py-20" style={{ backgroundColor: theme.lightBgHex }}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Areas of Expertise
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Specialized training and experience in
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {expertise.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 bg-white p-5 rounded-xl shadow-sm"
              >
                <Stethoscope className="w-6 h-6 flex-shrink-0" style={{ color: theme.primaryHex }} />
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section - Meet Dr. Reddy */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Meet Dr. Reddy
              </h2>
              <p className="text-lg text-gray-600">
                Hear directly from Dr. Reddy about his approach to patient care
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <YouTubeEmbed videoId={DR_REDDY_VIDEOS.aboutDrReddy.id} title={DR_REDDY_VIDEOS.aboutDrReddy.title} />
                <p className="text-sm text-gray-500 mt-3 text-center">Introduction to Dr. Reddy</p>
              </div>
              <div>
                <YouTubeEmbed videoId={DR_REDDY_VIDEOS.orthopedicScience.id} title={DR_REDDY_VIDEOS.orthopedicScience.title} />
                <p className="text-sm text-gray-500 mt-3 text-center">The Science Behind Orthopedics</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20" style={{ backgroundColor: theme.primaryHex }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Quote className="w-16 h-16 mx-auto mb-6 opacity-30" />
            <blockquote className="text-2xl md:text-3xl font-light italic mb-8 leading-relaxed">
              &ldquo;I love being able to help people regain their mobility and get back to the things they love. 
              Every patient deserves world-class care, regardless of where they are.&rdquo;
            </blockquote>
            <p className="text-xl">— {doctor.name}</p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Book Your Consultation
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2" style={{ borderColor: theme.primaryHex }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin style={{ color: theme.primaryHex }} />
                    Hospital Location
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-gray-900">{doctor.hospitalFull}</p>
                  <p className="text-gray-600 text-sm mt-1">{doctor.address}</p>
                  <a 
                    href={doctor.googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-sm font-medium"
                    style={{ color: theme.primaryHex }}
                  >
                    Get Directions <ExternalLink className="w-3 h-3" />
                  </a>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock style={{ color: theme.primaryHex }} />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <a href={`tel:${doctor.phone}`} className="font-medium" style={{ color: theme.primaryHex }}>
                      {doctor.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <a href={`mailto:${doctor.email}`} className="font-medium" style={{ color: theme.primaryHex }}>
                      {doctor.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-700">{doctor.timings}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12">
              <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
                <Button size="lg" style={{ backgroundColor: theme.primaryHex }} className="text-white">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
